import { Dispatch, SetStateAction, useEffect, useState } from 'react'

function load<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>, hostPrefix: string): EntityT {
    if (props.key) {
        console.debug(`loading ${hostPrefix}${props.key} from localstorage`)
        let jsonValues: JsonT = JSON.parse(localStorage.getItem(`${hostPrefix}${props.key}`))
        if ([null, undefined].includes(jsonValues)) {
            console.debug(`${hostPrefix}${props.key} is empty in localstorage, attempting ${props.key}`)
            jsonValues = JSON.parse(localStorage.getItem(props.key))
            localStorage.removeItem(props.key)
        }
        if ([null, undefined].includes(jsonValues)) {
            console.debug(`${props.key} is empty in localstorage, falling back to defaultValues`)
            jsonValues = props.defaultValues as any
        }

        return jsonValues !== undefined ? props.fromJson(jsonValues) : ({} as any)
    }
    return props.defaultValues
}

export type LocalStorageState<EntityT, JsonT = EntityT> = {
    key?: string // When key is undefined, this hook acts as a useState hook.
    hostKey?: string
    readOnly?: boolean
    defaultValues?: EntityT
    fromJson: (json: JsonT) => EntityT
    toJson: (values: EntityT) => JsonT
}

export type LocalStorageResult<EntityT> = {
    values: EntityT
    setValues: Dispatch<SetStateAction<EntityT>>
}

/**
 * This hook will load & persist the values to the LocalStorage using the given key. When a default value is provided it
 * will return this when LocalStorage is empty. When no key is provided, this hook acts as a useState hook.
 */
export default function useLocalStorage<EntityT, JsonT = EntityT>(props: LocalStorageState<EntityT, JsonT>): LocalStorageResult<EntityT> {
    const [hostPrefix] = useState<string>(() => (props.hostKey ? `${props.hostKey}-` : ''))
    const [values, setValues] = useState<EntityT>(() => load(props, hostPrefix))

    useEffect(() => {
        if (props.key) {
            window.addEventListener('storage', (evt: StorageEvent) => {
                console.debug(`localstorage ${hostPrefix}${props.key} changed, loading`)
                if (evt.key === `${hostPrefix}${props.key}`) {
                    setValues(load(props, hostPrefix))
                }
            })
        }
    }, [])

    useEffect(() => {
        if (props.readOnly) {
            console.debug(`values ${hostPrefix}${props.key} changed, but it's readOnly`)
        } else if (props.key) {
            console.debug(`values ${hostPrefix}${props.key} changed, storing`)
            localStorage.setItem(`${hostPrefix}${props.key}`, JSON.stringify(props.toJson(values)))
        }
    }, [values])

    return { values, setValues }
}
